'use strict';

/**
 * PictView-Project: the workspace for a Project (V54) -- a unit of work built from a cluster of source files.
 * A Project gathers a corpus (ProjectSource items, uploaded files and inline data), the conversational tasks
 * run against it (which open in the Ask area, carrying IDProject), and its artifacts (a Blueprint, platform
 * outputs). The plan sheet is the series or domain; a Project is one unit inside it; there are many per plan
 * sheet. Two routes: /projects (the list) and /project/:id (the workspace); the body repaints into
 * #Project-Body so a corpus change does not re-render the shell.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libFrameworkClient = require('../providers/FrameworkClient.js');

const _TRASH_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>';

// The project's general instructions (a priming statement that leads every task run against it) are
// stored as one reserved-role inline-DATA ProjectSource, so they fold into the dispatch corpus like any
// other source and need no column of their own. The UI shows them in a dedicated box, not the file list.
const _INSTRUCTIONS_ROLE = 'Instructions';

class PictViewProject extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._projects = [];
		this._project = null;
		this._corpus = [];
		this._instructionsSource = null;
		this._tasks = [];
		this._requests = [];
		this._sessions = [];
		// The configurable metadata form (a manyfest manifest resolved per Kind) and the project's own
		// MetadataJSON blob it reads and writes, plus the work-item inputs (linked records that ride with a
		// run) and a lazily fetched candidate list for the input picker.
		this._metaManifest = null;
		this._metaSource = null;
		this._metadata = {};
		this._workItemInputs = [];
		this._inputCandidates = null;
		this._busy = false;
	}

	_projectAPI() { return this.pict.providers.ProjectAPI; }
	_sourceAPI() { return this.pict.providers.ProjectSourceAPI; }
	_mediaAPI() { return this.pict.providers.MediaAPI; }
	_taskAPI() { return this.pict.providers.TaskAPI; }
	_requestAPI() { return this.pict.providers.RequestAPI; }
	_askAPI() { return this.pict.providers.AskAPI; }
	_sessionAPI() { return this.pict.providers.SessionAPI; }
	_connectionsAPI() { return this.pict.providers.ConnectionsAPI; }
	_router() { return this.pict.providers['Pict-Router']; }

	_esc(pValue)
	{
		return String(pValue === null || typeof pValue === 'undefined' ? '' : pValue)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	_body() { return (typeof document !== 'undefined') ? document.getElementById('Project-Body') : null; }
	_paint(pHTML) { let tmpBody = this._body(); if (tmpBody) { tmpBody.innerHTML = pHTML; } }
	_app() { return this.pict.PictApplication; }
	_ensureShell() { if (!this._body()) { this.render(); } }

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && tmpModal.toast) { tmpModal.toast(pMessage, { type: pType || 'success' }); }
	}

	_pill(pStatus)
	{
		let tmpStatus = String(pStatus || 'Draft');
		// Slug the class to a safe charset -- Status is a free VARCHAR a teammate can set, so it must never reach
		// the class attribute raw (it would break out and run as stored XSS).
		let tmpSlug = tmpStatus.toLowerCase().replace(/[^a-z0-9]/g, '');
		return '<span class="prj-pill prj-pill-' + tmpSlug + '">' + this._esc(tmpStatus) + '</span>';
	}

	show() { return this.showList(); }

	// ---- the list (route /projects) ------------------------------------------------------------------------
	showList()
	{
		this._ensureShell();
		this._enterList();
		return true;
	}

	async _enterList()
	{
		this._project = null;
		this._paint('<div class="prj-loading">Loading projects...</div>');
		let tmpProjects = [];
		try { tmpProjects = await this._projectAPI().resolve({ PageSize: 200 }); }
		catch (pError) { /* a transient failure paints an empty list rather than a blank pane */ }
		this._projects = (Array.isArray(tmpProjects) ? tmpProjects : [])
			.sort((pA, pB) => (Number(pB.ProjectNumber) || 0) - (Number(pA.ProjectNumber) || 0));
		this._paintList();
	}

	_paintList()
	{
		let tmpCards = this._projects.map((pProject) => ''
			+ '<button class="prj-card" onclick="_Pict.views[\'Plansheet-Project\'].openProject(' + (Number(pProject.IDProject) || 0) + ')">'
			+ '<div class="prj-card-head"><span class="prj-card-name">' + this._esc(pProject.Name || ('Project ' + (pProject.ProjectNumber || ''))) + '</span>' + this._pill(pProject.Status) + '</div>'
			+ (pProject.Kind ? ('<div class="prj-card-kind">' + this._esc(pProject.Kind) + '</div>') : '')
			+ (pProject.Summary ? ('<div class="prj-card-sum">' + this._esc(pProject.Summary) + '</div>') : '')
			+ '</button>').join('');
		this._paint(''
			+ '<div class="prj-new">'
			+ '<div class="prj-group-head">New project</div>'
			+ '<div class="prj-new-row">'
			+ '<input type="text" id="Project-NewName" class="prj-input" placeholder="Project name (e.g. Materials Inspection)" />'
			+ '<input type="text" id="Project-NewKind" class="prj-input prj-input-kind" value="form" placeholder="kind" />'
			+ '<button class="prj-btn" onclick="_Pict.views[\'Plansheet-Project\'].createProject()">Create</button>'
			+ '</div></div>'
			+ (tmpCards ? ('<div class="prj-cards">' + tmpCards + '</div>') : '<div class="prj-empty">No projects yet. Create one above to start building from a cluster of files.</div>'));
	}

	async createProject()
	{
		if (this._busy) { return; }
		let tmpNameEl = (typeof document !== 'undefined') ? document.getElementById('Project-NewName') : null;
		let tmpKindEl = (typeof document !== 'undefined') ? document.getElementById('Project-NewKind') : null;
		let tmpName = tmpNameEl ? String(tmpNameEl.value || '').trim() : '';
		let tmpKind = tmpKindEl ? String(tmpKindEl.value || '').trim() : '';
		if (!tmpName) { this._toast('Give the project a name first.', 'error'); return; }
		this._busy = true;
		let tmpProject = null;
		try { tmpProject = await this._projectAPI().create({ Name: tmpName, Kind: tmpKind || 'form', Status: 'Draft' }); }
		catch (pError) { this._busy = false; this._toast((pError && pError.message) || 'Could not create the project.', 'error'); return; }
		this._busy = false;
		let tmpID = Number(tmpProject && tmpProject.IDProject) || 0;
		if (tmpID) { this._goDetail(tmpID); } else { this._enterList(); }
	}

	// ---- the workspace (route /project/:id) ----------------------------------------------------------------
	openProject(pID) { this._goDetail(pID); }

	async showDetail(pID)
	{
		this._ensureShell();
		let tmpID = Number(pID) || 0;
		this._paint('<div class="prj-loading">Loading...</div>');
		let tmpProject = null;
		try { tmpProject = await this._projectAPI().read(tmpID); }
		catch (pError) { tmpProject = null; }
		if (!tmpProject) { return this.showList(); }
		this._project = tmpProject;
		await this._loadDetailData(tmpID);
		this._paintDetail();
	}

	async _loadDetailData(pIDProject)
	{
		let tmpCorpus = [];
		let tmpTasks = [];
		let tmpRequests = [];
		let tmpSessions = [];
		try { tmpCorpus = await this._sourceAPI().resolve({ Filter: 'FBV~IDProject~EQ~' + pIDProject, PageSize: 200 }); } catch (pError) { tmpCorpus = []; }
		try { tmpTasks = await this._taskAPI().resolve({ Filter: 'FBV~Status~EQ~Active', PageSize: 250 }); } catch (pError) { tmpTasks = []; }
		try { tmpRequests = await this._requestAPI().resolve({ Filter: 'FBV~IDProject~EQ~' + pIDProject, PageSize: 50 }); } catch (pError) { tmpRequests = []; }
		try { tmpSessions = await this._sessionAPI().projectSessions(pIDProject); } catch (pError) { tmpSessions = []; }
		this._applyCorpus(tmpCorpus);
		// Only conversational tasks are runnable chain steps in a Project.
		this._tasks = (Array.isArray(tmpTasks) ? tmpTasks : []).filter((pTask) => pTask && pTask.Conversational);
		this._requests = (Array.isArray(tmpRequests) ? tmpRequests : []).sort((pA, pB) => (Number(pB.RequestNumber) || 0) - (Number(pA.RequestNumber) || 0));
		this._sessions = Array.isArray(tmpSessions) ? tmpSessions : [];

		// The metadata blob the project owns, and the form that renders it. Parse the stored blob first; an
		// older project kept its priming statement as the reserved-role 'Instructions' source, so seed that
		// into the blob view when the blob has none (a save then migrates it -- see saveMetadata). _applyCorpus
		// ran above, so this._instructionsSource is set before this seed reads it.
		this._metadata = {};
		try { let tmpParsed = JSON.parse((this._project && this._project.MetadataJSON) || 'null'); if (tmpParsed && typeof tmpParsed === 'object') { this._metadata = tmpParsed; } } catch (pIgnore) { this._metadata = {}; }
		if (!this._metadata.Instructions && this._instructionsSource) { let tmpLegacy = this._instructionsText(); if (tmpLegacy) { this._metadata.Instructions = tmpLegacy; } }
		this._metaManifest = null;
		this._metaSource = null;
		this._inputCandidates = null;
		// Resolve the configured metadata form and gather the work-item inputs concurrently -- neither blocks
		// the other, and a failure in either leaves a usable default rather than a blank panel.
		let tmpKindSeg = encodeURIComponent(String((this._project && this._project.Kind) || '').trim() || '-');
		let tmpManifestP = libFrameworkClient.getJSON(this.pict, '/1.0/MetadataManifest/Resolve/Project/' + tmpKindSeg).catch(() => null);
		let tmpConnP = this._connectionsAPI() ? this._connectionsAPI().get('Project', pIDProject) : Promise.resolve(null);
		let tmpResolved = await tmpManifestP;
		if (tmpResolved && tmpResolved.Manifest && typeof tmpResolved.Manifest === 'object') { this._metaManifest = tmpResolved.Manifest; this._metaSource = tmpResolved.Source || null; }
		if (!this._metaManifest) { this._metaManifest = { Descriptors: {}, Sections: [] }; }
		this._workItemInputs = this._extractInputs(await tmpConnP);
	}

	// Split the fetched sources into the file/data corpus (shown in the Corpus list) and the one reserved
	// Instructions source (shown in its own box). Both the first load and a reload pass through here so the
	// instructions item never leaks into the file list.
	_applyCorpus(pSources)
	{
		let tmpSorted = (Array.isArray(pSources) ? pSources : []).sort((pA, pB) => (Number(pA.SortOrder) || 0) - (Number(pB.SortOrder) || 0));
		this._instructionsSource = tmpSorted.find((pSource) => String(pSource.Role || '') === _INSTRUCTIONS_ROLE && !(Number(pSource.IDMedia) > 0)) || null;
		this._corpus = tmpSorted.filter((pSource) => pSource !== this._instructionsSource);
	}

	// The current instructions text (the reserved source stores it as a JSON string in ContentJSON).
	_instructionsText()
	{
		let tmpSource = this._instructionsSource;
		if (!tmpSource) { return ''; }
		try { let tmpValue = JSON.parse(tmpSource.ContentJSON || '""'); return (typeof tmpValue === 'string') ? tmpValue : String(tmpValue || ''); }
		catch (pError) { return String(tmpSource.ContentJSON || ''); }
	}

	async _reloadCorpus()
	{
		if (!this._project) { return; }
		try { this._applyCorpus(await this._sourceAPI().resolve({ Filter: 'FBV~IDProject~EQ~' + this._project.IDProject, PageSize: 200 })); }
		catch (pError) { /* keep the last corpus on a transient failure */ }
		let tmpEl = (typeof document !== 'undefined') ? document.getElementById('Project-Corpus') : null;
		if (tmpEl) { tmpEl.innerHTML = this._corpusInnerHTML(); }
	}

	_corpusInnerHTML()
	{
		let tmpRows = this._corpus.map((pSource) =>
		{
			let tmpIsFile = Number(pSource.IDMedia) > 0;
			return ''
				+ '<div class="prj-src">'
				+ '<span class="prj-role">' + this._esc(pSource.Role || 'Source') + '</span>'
				+ '<span class="prj-src-name">' + this._esc(pSource.Name || (tmpIsFile ? 'file' : 'data')) + '</span>'
				+ '<span class="prj-src-kind">' + (tmpIsFile ? 'file' : 'data') + '</span>'
				+ '<button class="prj-src-del" title="Remove" aria-label="Remove" onclick="_Pict.views[\'Plansheet-Project\'].removeSource(' + (Number(pSource.IDProjectSource) || 0) + ')">' + _TRASH_SVG + '</button>'
				+ '</div>';
		}).join('');
		return (tmpRows || '<div class="prj-empty-sm">No files yet. Add the template, the filled examples, the policy...</div>')
			+ '<div class="prj-add">'
			+ '<input type="file" id="Project-File" class="prj-file" multiple onchange="_Pict.views[\'Plansheet-Project\'].attachFiles(this)" />'
			+ '<input type="text" id="Project-FileRole" class="prj-input prj-input-role" value="Reference" placeholder="role" />'
			+ '<button class="prj-btn prj-btn-ghost" onclick="document.getElementById(\'Project-File\').click()">Attach files</button>'
			+ '</div>'
			+ '<div class="prj-add">'
			+ '<input type="text" id="Project-DataRole" class="prj-input prj-input-role" value="Data" placeholder="role" />'
			+ '<input type="text" id="Project-DataName" class="prj-input prj-input-role" placeholder="name" />'
			+ '<textarea id="Project-DataJSON" class="prj-input prj-data-json" rows="2" placeholder="inline JSON data"></textarea>'
			+ '<button class="prj-btn prj-btn-ghost" onclick="_Pict.views[\'Plansheet-Project\'].addData()">Add data</button>'
			+ '</div>';
	}

	_paintDetail()
	{
		let tmpProject = this._project || {};
		let tmpTaskButtons = this._tasks.length
			? this._tasks.map((pTask) => '<button class="prj-btn" onclick="_Pict.views[\'Plansheet-Project\'].runTask(' + (Number(pTask.IDTask) || 0) + ')">' + this._esc(pTask.Name || 'Run') + '</button>').join('')
			: '<div class="prj-empty-sm">No conversational tasks are set up in this plan sheet yet.</div>';
		let tmpReqRows = this._requests.length
			? this._requests.map((pRequest) => ''
				+ '<button class="prj-req" onclick="_Pict.views[\'Plansheet-Project\'].openRequest(' + (Number(pRequest.IDRequest) || 0) + ')">'
				+ '<span class="prj-req-name">' + this._esc(pRequest.Name || ('Request ' + (pRequest.RequestNumber || ''))) + '</span>' + this._pill(pRequest.Status)
				+ '</button>').join('')
			: '<div class="prj-empty-sm">No runs yet. Launch a task above.</div>';
		let tmpBlueprint = (Number(tmpProject.IDBlueprint) > 0)
			? '<button class="prj-btn" onclick="_Pict.views[\'Plansheet-Project\'].openBlueprint()">Open the blueprint</button>'
			: '<div class="prj-empty-sm">No blueprint yet. Run a generate-blueprint task.</div>';
		let tmpOutputs = this._outputsHTML(tmpProject);

		this._paint(''
			+ '<div class="prj-detail">'
			+ '<button class="prj-back" onclick="_Pict.views[\'Plansheet-Project\'].backToList()">&larr; All projects</button>'
			+ '<div class="prj-head"><h2 class="prj-title">' + this._esc(tmpProject.Name || 'Project') + '</h2>'
			+ (tmpProject.Kind ? ('<span class="prj-kind-chip">' + this._esc(tmpProject.Kind) + '</span>') : '') + this._pill(tmpProject.Status) + '</div>'
			+ (tmpProject.Summary ? ('<div class="prj-sum">' + this._esc(tmpProject.Summary) + '</div>') : '')
			+ this._proposalPanelHTML(tmpProject)
			+ '<div class="prj-panel"><div class="prj-group-head">Project metadata</div><div id="Project-Meta">' + this._metaFormHTML() + '</div>'
			+ '<div class="prj-add"><button class="prj-btn prj-btn-ghost" onclick="_Pict.views[\'Plansheet-Project\'].saveMetadata()">Save metadata</button><span id="Project-Meta-State" class="prj-hint"></span></div>'
			+ '<div class="prj-hint">These fields are configured per project kind; they travel with every task run on this project.</div></div>'
			+ '<div class="prj-panel"><div class="prj-group-head">Corpus</div><div id="Project-Corpus">' + this._corpusInnerHTML() + '</div></div>'
			+ '<div class="prj-panel"><div class="prj-group-head">Work item inputs</div><div id="Project-Inputs">' + this._inputsHTML() + '</div>'
			+ '<div class="prj-hint">Linked work items ride along as inputs a task can read -- title, status, and description.</div></div>'
			+ '<div class="prj-panel"><div class="prj-group-head">Run a task</div><div class="prj-tasks">' + tmpTaskButtons + '</div>'
			+ '<div class="prj-hint">A task runs against the whole corpus and opens as a conversation in the Ask area.</div></div>'
			+ '<div class="prj-panel"><div class="prj-group-head">Conversations</div><div class="prj-reqs">' + tmpReqRows + '</div></div>'
			+ '<div class="prj-panel"><div class="prj-group-head">Agent sessions</div>' + this._sessionsHTML() + '</div>'
			+ '<div class="prj-panel"><div class="prj-group-head">Blueprint</div>' + tmpBlueprint + '</div>'
			+ (tmpOutputs ? ('<div class="prj-panel"><div class="prj-group-head">Outputs</div>' + tmpOutputs + '</div>') : '')
			+ '</div>');
	}

	// When a type-authoring turn has proposed a Project type, it is staged on this Project's OutputsJSON
	// (Run-Guards). Show it with a Create/Publish action: that POST is the person's consent, and only then is the
	// type committed (a new MetadataManifest Version + the ProjectType). Until then the proposal is inert.
	_proposalPanelHTML(pProject)
	{
		let tmpOutputs = null;
		try { tmpOutputs = JSON.parse((pProject && pProject.OutputsJSON) || 'null'); } catch (pError) { tmpOutputs = null; }
		let tmpProposal = (tmpOutputs && typeof tmpOutputs === 'object') ? tmpOutputs.ProjectTypeProposal : null;
		if (!tmpProposal || !tmpProposal.Manifest || typeof tmpProposal.Manifest !== 'object') { return ''; }
		let tmpManifest = tmpProposal.Manifest;
		let tmpDescriptors = (tmpManifest && typeof tmpManifest.Descriptors === 'object') ? tmpManifest.Descriptors : {};
		let tmpSections = Array.isArray(tmpManifest.Sections) ? tmpManifest.Sections : [];
		let tmpFieldKeys = Object.keys(tmpDescriptors);
		let tmpFields = tmpFieldKeys.map((pKey) =>
		{
			let tmpDescriptor = tmpDescriptors[pKey] || {};
			let tmpForm = tmpDescriptor.PictForm || {};
			return '<div class="prj-out"><span class="prj-out-key">' + this._esc(tmpDescriptor.Name || pKey) + '</span>'
				+ '<span class="prj-out-val">' + this._esc((tmpForm.InputType || 'Text') + (tmpForm.Section ? (' in ' + tmpForm.Section) : '')) + '</span></div>';
		}).join('');
		let tmpIsAdjust = Number(tmpProposal.IDProjectType) > 0;
		return ''
			+ '<div class="prj-panel">'
			+ '<div class="prj-group-head">Proposed project type' + (tmpIsAdjust ? ' (adjustment)' : '') + '</div>'
			+ '<div class="prj-head"><span class="prj-title" style="font-size:1.12em;">' + this._esc(tmpProposal.Name || tmpProposal.TypeKey || 'Untitled type') + '</span>'
			+ (tmpProposal.TypeKey ? ('<span class="prj-kind-chip">' + this._esc(tmpProposal.TypeKey) + '</span>') : '') + '</div>'
			+ (tmpProposal.Description ? ('<div class="prj-sum">' + this._esc(tmpProposal.Description) + '</div>') : '')
			+ '<div class="prj-hint">' + tmpFieldKeys.length + ' field' + (tmpFieldKeys.length === 1 ? '' : 's') + ' in ' + tmpSections.length + ' section' + (tmpSections.length === 1 ? '' : 's') + '. Review it, then create the type when you are happy.</div>'
			+ (tmpFields ? ('<div class="prj-reqs" style="margin-top:8px;">' + tmpFields + '</div>') : '')
			+ '<div class="prj-add"><button class="prj-btn" onclick="_Pict.views[\'Plansheet-Project\'].commitProjectType()">' + (tmpIsAdjust ? 'Publish the new version' : 'Create this type') + '</button><span id="Project-Proposal-State" class="prj-hint"></span></div>'
			+ '</div>';
	}

	async commitProjectType()
	{
		if (this._busy || !this._project) { return; }
		let tmpIDProject = Number(this._project.IDProject) || 0;
		if (!tmpIDProject) { return; }
		this._busy = true;
		let tmpState = (typeof document !== 'undefined') ? document.getElementById('Project-Proposal-State') : null;
		if (tmpState) { tmpState.textContent = 'Creating the type...'; }
		let tmpResult = null;
		try { tmpResult = await libFrameworkClient.postJSON(this.pict, '/1.0/ProjectType/Commit', { IDProject: tmpIDProject }); }
		catch (pError) { this._busy = false; if (tmpState) { tmpState.textContent = ''; } this._toast((pError && pError.message) || 'Could not create the type.', 'error'); return; }
		this._busy = false;
		this._toast('Project type created.', 'success');
		let tmpIDType = Number(tmpResult && tmpResult.ProjectType && tmpResult.ProjectType.IDProjectType) || 0;
		let tmpApp = this._app();
		if (tmpIDType && tmpApp && typeof tmpApp.showProjectType === 'function') { tmpApp.showProjectType(tmpIDType); }
		else { this.showDetail(tmpIDProject); }
	}

	_outputsHTML(pProject)
	{
		let tmpOutputs = null;
		try { tmpOutputs = JSON.parse((pProject && pProject.OutputsJSON) || 'null'); } catch (pError) { tmpOutputs = null; }
		if (!tmpOutputs || typeof tmpOutputs !== 'object' || !Object.keys(tmpOutputs).length) { return ''; }
		// ProjectTypeProposal has its own panel above; do not also dump it here as raw JSON.
		let tmpRows = Object.keys(tmpOutputs).filter((pKey) => pKey !== 'ProjectTypeProposal').map((pKey) =>
		{
			let tmpValue = tmpOutputs[pKey];
			let tmpUrl = (tmpValue && typeof tmpValue === 'object') ? (tmpValue.url || tmpValue.URL) : null;
			// Only link an http(s) URL -- the value comes from node output, so a javascript:/data: scheme would be a
			// clickable XSS. Anything else renders as escaped text.
			let tmpSafeUrl = (tmpUrl && /^https?:\/\//i.test(String(tmpUrl))) ? String(tmpUrl) : '';
			let tmpShown = tmpSafeUrl
				? ('<a class="prj-out-link" href="' + this._esc(tmpSafeUrl) + '" target="_blank" rel="noopener noreferrer">' + this._esc(tmpSafeUrl) + '</a>')
				: this._esc(typeof tmpValue === 'object' ? JSON.stringify(tmpValue) : tmpValue);
			return '<div class="prj-out"><span class="prj-out-key">' + this._esc(pKey) + '</span><span class="prj-out-val">' + tmpShown + '</span></div>';
		}).join('');
		return tmpRows;
	}

	// The Claude sessions that built this Project (the runner's whole transcript, filed and linked
	// to the Project), newest first. Each row opens the session in the viewer at its own route.
	_sessionsHTML()
	{
		let tmpSessions = Array.isArray(this._sessions) ? this._sessions : [];
		if (!tmpSessions.length) { return '<div class="prj-empty-sm">No sessions filed yet. A run files its session here when it finishes.</div>'; }
		return tmpSessions.map((pSession) =>
		{
			let tmpID = Number(pSession.IDSessionFile) || 0;
			let tmpWhen = String(pSession.SessionStartedAt || pSession.CreateDate || '').slice(0, 16).replace('T', ' ');
			let tmpMins = Math.round((Number(pSession.TotalDurationMs) || 0) / 60000);
			let tmpMeta = [
				tmpWhen,
				(Number(pSession.PromptCount) || 0) + ' prompts',
				(Number(pSession.ToolCallCount) || 0) + ' tools',
				(tmpMins > 0 ? (tmpMins + ' min') : ''),
				this._esc(pSession.Model || '')
			].filter((pPart) => String(pPart || '').trim()).join(' &middot; ');
			return ''
				+ '<button class="prj-req" onclick="_Pict.views[\'Plansheet-Project\']._openSession(' + tmpID + ')">'
				+ '<span class="prj-req-name">' + this._esc(pSession.Title || ('Session #' + tmpID)) + '</span>'
				+ '<span class="prj-src-kind">' + tmpMeta + '</span>'
				+ '</button>';
		}).join('');
	}

	_openSession(pIDSessionFile)
	{
		let tmpID = Number(pIDSessionFile) || 0;
		if (!tmpID) { return; }
		let tmpApp = this._app();
		if (tmpApp && typeof tmpApp.showSession === 'function') { tmpApp.showSession(tmpID); }
		else if (tmpApp && typeof tmpApp.openServerSession === 'function') { tmpApp.openServerSession(tmpID); }
	}

	// ---- corpus actions ------------------------------------------------------------------------------------
	// Run one authenticated write with a single retry. The upload path (binary blob PUT, then the corpus
	// create) has been seen to drop intermittently -- a transient 5xx, an auth slide, or a connection the
	// edge did not keep alive -- and leave an orphan Media row with an unhelpful toast. One retry turns most
	// of those into a success. A real client refusal (a 4xx that is not auth/timeout/rate) is not retried.
	async _retryWrite(fThunk)
	{
		try { return await fThunk(); }
		catch (pError)
		{
			let tmpStatus = Number(pError && pError.StatusCode) || 0;
			if (tmpStatus >= 400 && tmpStatus < 500 && tmpStatus !== 401 && tmpStatus !== 408 && tmpStatus !== 429) { throw pError; }
			return await fThunk();
		}
	}

	// A toast-ready description of a failed write that keeps the HTTP status, so a recurring upload failure
	// names itself (413 = too large / proxy cap, 5xx = server, 401 = auth) instead of reading as a mystery.
	_writeErr(pError)
	{
		let tmpStatus = Number(pError && pError.StatusCode) || 0;
		let tmpMessage = (pError && pError.message) || 'unknown error';
		return tmpMessage + (tmpStatus ? (' (HTTP ' + tmpStatus + ')') : '');
	}

	// ---- configurable metadata form ------------------------------------------------------------------------
	// The metadata form is driven entirely by the manifest the server resolved for this project's Kind: each
	// descriptor is a leaf scalar in MetadataJSON, and its PictForm hints (Section, Row, 12-col Width, InputType)
	// place it. A plan sheet reshapes the form by authoring a MetadataManifest row; no code changes here. This
	// is a light hand-rendered form rather than an embedded pict-section-form -- the whole view paints HTML into
	// one node, and a manyfest of leaf scalars needs a renderer, not a second form application and its deps.
	_slug(pKey) { return String(pKey || '').replace(/[^a-zA-Z0-9]/g, '_'); }
	_asText(pValue) { return (pValue === null || typeof pValue === 'undefined') ? '' : String(pValue); }
	_asBool(pValue) { return pValue === true || pValue === 1 || pValue === '1' || String(pValue).toLowerCase() === 'true'; }
	_metaValue(pKey) { return this._getPath(this._metadata, pKey); }

	// A descriptor address may be a dot path (manyfest nests), so read and write MetadataJSON by path rather
	// than by a flat key -- a flat key is just a one-segment path and still works.
	_getPath(pObject, pPath)
	{
		let tmpParts = String(pPath || '').split('.');
		let tmpCursor = pObject;
		for (let i = 0; i < tmpParts.length; i++)
		{
			if (tmpCursor === null || typeof tmpCursor !== 'object') { return undefined; }
			tmpCursor = tmpCursor[tmpParts[i]];
		}
		return tmpCursor;
	}

	_setPath(pObject, pPath, pValue)
	{
		let tmpParts = String(pPath || '').split('.');
		let tmpCursor = pObject;
		for (let i = 0; i < tmpParts.length - 1; i++)
		{
			let tmpKey = tmpParts[i];
			if (tmpCursor[tmpKey] === null || typeof tmpCursor[tmpKey] !== 'object') { tmpCursor[tmpKey] = {}; }
			tmpCursor = tmpCursor[tmpKey];
		}
		tmpCursor[tmpParts[tmpParts.length - 1]] = pValue;
	}

	_metaFormHTML()
	{
		let tmpManifest = this._metaManifest || {};
		let tmpDescriptors = (tmpManifest.Descriptors && typeof tmpManifest.Descriptors === 'object') ? tmpManifest.Descriptors : {};
		let tmpKeys = Object.keys(tmpDescriptors);
		if (!tmpKeys.length) { return '<div class="prj-empty-sm">No metadata fields are configured for this kind.</div>'; }

		// Render sections in the order the manifest declares them; a descriptor whose section is unknown falls
		// into the first declared section so nothing is silently dropped.
		let tmpSectionList = Array.isArray(tmpManifest.Sections) ? tmpManifest.Sections : [];
		let tmpSectionOrder = tmpSectionList.map((pSection) => String(pSection.Hash || ''));
		let tmpSectionName = {};
		tmpSectionList.forEach((pSection) => { tmpSectionName[String(pSection.Hash || '')] = String(pSection.Name || ''); });

		let tmpBuckets = {};
		tmpKeys.forEach((pKey, pIndex) =>
		{
			let tmpDescriptor = tmpDescriptors[pKey] || {};
			let tmpForm = tmpDescriptor.PictForm || {};
			let tmpSection = String(tmpForm.Section || '');
			if (tmpSectionOrder.indexOf(tmpSection) < 0) { tmpSection = tmpSectionOrder[0] || ''; }
			if (!tmpBuckets[tmpSection]) { tmpBuckets[tmpSection] = []; }
			tmpBuckets[tmpSection].push({ Key: pKey, Descriptor: tmpDescriptor, Form: tmpForm, Row: Number(tmpForm.Row) || 0, Seq: pIndex });
		});

		let tmpToRender = tmpSectionOrder.length ? tmpSectionOrder.slice() : Object.keys(tmpBuckets);
		Object.keys(tmpBuckets).forEach((pSection) => { if (tmpToRender.indexOf(pSection) < 0) { tmpToRender.push(pSection); } });

		return tmpToRender.map((pSection) =>
		{
			let tmpFields = tmpBuckets[pSection];
			if (!tmpFields || !tmpFields.length) { return ''; }
			tmpFields.sort((pA, pB) => (pA.Row - pB.Row) || (pA.Seq - pB.Seq));
			let tmpName = tmpSectionName[pSection] || '';
			// The panel header already names a single section, so only label a section when there is more than one.
			let tmpHeading = (tmpName && tmpToRender.length > 1) ? ('<div class="prj-meta-section">' + this._esc(tmpName) + '</div>') : '';
			return tmpHeading + '<div class="prj-meta-grid">' + tmpFields.map((pField) => this._metaFieldHTML(pField)).join('') + '</div>';
		}).join('');
	}

	_metaFieldHTML(pField)
	{
		let tmpKey = pField.Key;
		let tmpDescriptor = pField.Descriptor || {};
		let tmpForm = pField.Form || {};
		let tmpDomID = 'ProjectMeta-' + this._slug(tmpKey);
		let tmpWidth = Math.max(1, Math.min(12, Number(tmpForm.Width) || 12));
		let tmpLabel = this._esc(tmpDescriptor.Name || tmpKey);
		let tmpHint = tmpDescriptor.Description ? ('<div class="prj-meta-hint">' + this._esc(tmpDescriptor.Description) + '</div>') : '';
		let tmpValue = this._metaValue(tmpKey);
		let tmpInputType = String(tmpForm.InputType || '').toLowerCase();
		let tmpDataType = String(tmpDescriptor.DataType || '').toLowerCase();
		let tmpControl = '';
		if (tmpInputType === 'textarea')
		{
			let tmpRows = Math.max(2, Math.min(40, Number(tmpForm.TextAreaRows) || 4));
			tmpControl = '<textarea id="' + tmpDomID + '" class="prj-input prj-meta-input" rows="' + tmpRows + '">' + this._esc(this._asText(tmpValue)) + '</textarea>';
		}
		else if (tmpInputType === 'select' || tmpInputType === 'dropdown')
		{
			tmpControl = '<select id="' + tmpDomID + '" class="prj-input prj-meta-input">' + this._metaOptionsHTML(tmpForm, tmpValue) + '</select>';
		}
		else if (tmpInputType === 'checkbox' || tmpDataType === 'boolean')
		{
			tmpControl = '<input type="checkbox" id="' + tmpDomID + '" class="prj-meta-check"' + (this._asBool(tmpValue) ? ' checked' : '') + ' />';
		}
		else if (tmpInputType === 'number' || tmpDataType === 'number' || tmpDataType === 'integer')
		{
			tmpControl = '<input type="number" id="' + tmpDomID + '" class="prj-input prj-meta-input" value="' + this._esc(this._asText(tmpValue)) + '" />';
		}
		else
		{
			let tmpHTMLType = (tmpInputType === 'date') ? 'date' : ((tmpInputType === 'email') ? 'email' : 'text');
			tmpControl = '<input type="' + tmpHTMLType + '" id="' + tmpDomID + '" class="prj-input prj-meta-input" value="' + this._esc(this._asText(tmpValue)) + '" />';
		}
		// Width is a clamped integer, never user text, so it is safe inline; the grid is 12 columns.
		return '<div class="prj-meta-field" style="grid-column: span ' + tmpWidth + ';">'
			+ '<label class="prj-meta-label" for="' + tmpDomID + '">' + tmpLabel + '</label>'
			+ tmpControl + tmpHint + '</div>';
	}

	_metaOptionsHTML(pForm, pValue)
	{
		let tmpOptions = Array.isArray(pForm.Options) ? pForm.Options : [];
		let tmpCurrent = this._asText(pValue);
		let fValueOf = (pOption) => (pOption && typeof pOption === 'object')
			? this._asText(pOption.Value !== undefined ? pOption.Value : (pOption.Id !== undefined ? pOption.Id : pOption.Hash))
			: this._asText(pOption);
		let fLabelOf = (pOption) => (pOption && typeof pOption === 'object')
			? this._asText(pOption.Label !== undefined ? pOption.Label : (pOption.Text !== undefined ? pOption.Text : fValueOf(pOption)))
			: this._asText(pOption);
		return '<option value=""' + (tmpCurrent === '' ? ' selected' : '') + '></option>'
			+ tmpOptions.map((pOption) =>
			{
				let tmpVal = fValueOf(pOption);
				return '<option value="' + this._esc(tmpVal) + '"' + (tmpVal === tmpCurrent ? ' selected' : '') + '>' + this._esc(fLabelOf(pOption)) + '</option>';
			}).join('');
	}

	// Collect the rendered fields back into the MetadataJSON blob and save it on the Project. Start from the
	// stored blob so keys the manifest does not mention are preserved, overlay each descriptor's value by its
	// path, then persist. A legacy reserved-role 'Instructions' source is retired once its text lives in the
	// blob, so there is one source of truth and it stops riding the corpus as a redundant item.
	async saveMetadata()
	{
		if (!this._project) { return; }
		let tmpStateEl = (typeof document !== 'undefined') ? document.getElementById('Project-Meta-State') : null;
		if (tmpStateEl) { tmpStateEl.textContent = 'Saving...'; }
		let tmpMerged = {};
		try { let tmpParsed = JSON.parse(this._project.MetadataJSON || 'null'); if (tmpParsed && typeof tmpParsed === 'object') { tmpMerged = tmpParsed; } } catch (pIgnore) { tmpMerged = {}; }
		let tmpDescriptors = (this._metaManifest && this._metaManifest.Descriptors && typeof this._metaManifest.Descriptors === 'object') ? this._metaManifest.Descriptors : {};
		Object.keys(tmpDescriptors).forEach((pKey) =>
		{
			let tmpDescriptor = tmpDescriptors[pKey] || {};
			let tmpForm = tmpDescriptor.PictForm || {};
			let tmpEl = (typeof document !== 'undefined') ? document.getElementById('ProjectMeta-' + this._slug(pKey)) : null;
			if (!tmpEl) { return; }
			let tmpInputType = String(tmpForm.InputType || '').toLowerCase();
			let tmpDataType = String(tmpDescriptor.DataType || '').toLowerCase();
			let tmpValue;
			if (tmpInputType === 'checkbox' || tmpDataType === 'boolean') { tmpValue = !!tmpEl.checked; }
			else if (tmpInputType === 'number' || tmpDataType === 'number' || tmpDataType === 'integer')
			{
				let tmpRaw = String(tmpEl.value || '').trim();
				tmpValue = (tmpRaw === '') ? null : Number(tmpRaw);
				if (tmpValue !== null && !isFinite(tmpValue)) { tmpValue = null; }
			}
			else { tmpValue = String(tmpEl.value || ''); }
			this._setPath(tmpMerged, pKey, tmpValue);
		});
		let tmpJSON = JSON.stringify(tmpMerged);
		try { await this._retryWrite(() => this._projectAPI().update(Number(this._project.IDProject) || 0, { MetadataJSON: tmpJSON })); }
		catch (pError) { if (tmpStateEl) { tmpStateEl.textContent = ''; } this._toast('Could not save metadata: ' + this._writeErr(pError), 'error'); return; }
		this._project.MetadataJSON = tmpJSON;
		this._metadata = tmpMerged;
		if (this._instructionsSource && Number(this._instructionsSource.IDProjectSource) > 0 && typeof tmpMerged.Instructions === 'string' && tmpMerged.Instructions.length)
		{
			let tmpLegacyID = Number(this._instructionsSource.IDProjectSource);
			this._instructionsSource = null;
			try { await this._sourceAPI().remove(tmpLegacyID); } catch (pIgnore) { /* best-effort: the blob already wins at dispatch time */ }
		}
		if (tmpStateEl) { tmpStateEl.textContent = 'Saved'; }
	}

	// ---- work item inputs ----------------------------------------------------------------------------------
	// Inputs are the work items linked to this project (a ProjectWorkItemJoin with RelationType 'Input'),
	// surfaced through the generic Connections endpoint. The dispatch hydrates each one's title, status and
	// description into the run envelope, so they are inputs in substance, not in name only.
	_extractInputs(pConnections)
	{
		let tmpGroups = (pConnections && Array.isArray(pConnections.Groups)) ? pConnections.Groups : [];
		let tmpGroup = tmpGroups.filter((pGroup) => String(pGroup.Type || '') === 'workiteminputs')[0];
		return (tmpGroup && Array.isArray(tmpGroup.Items)) ? tmpGroup.Items : [];
	}

	_inputsHTML()
	{
		let tmpItems = Array.isArray(this._workItemInputs) ? this._workItemInputs : [];
		let tmpRows = tmpItems.map((pItem) =>
		{
			let tmpID = Number(pItem.ID) || 0;
			return ''
				+ '<div class="prj-src"><span class="prj-role">input</span>'
				+ '<button class="prj-src-name prj-src-link" onclick="_Pict.views[\'Plansheet-Project\'].openWorkItem(' + tmpID + ')">' + this._esc(pItem.Title || ('Work item #' + tmpID)) + '</button>'
				+ '<button class="prj-src-del" title="Remove" aria-label="Remove" onclick="_Pict.views[\'Plansheet-Project\'].removeInput(' + tmpID + ')">' + _TRASH_SVG + '</button>'
				+ '</div>';
		}).join('');
		return (tmpRows || '<div class="prj-empty-sm">No work items linked as inputs yet.</div>')
			+ '<div class="prj-add">'
			+ '<select id="Project-InputPick" class="prj-input prj-input-pick" onmousedown="_Pict.views[\'Plansheet-Project\'].loadInputCandidates()">' + this._inputOptionsHTML() + '</select>'
			+ '<button class="prj-btn prj-btn-ghost" onclick="_Pict.views[\'Plansheet-Project\'].addInput()">Add input</button>'
			+ '</div>';
	}

	_inputOptionsHTML()
	{
		let tmpList = Array.isArray(this._inputCandidates) ? this._inputCandidates : [];
		let tmpLead = this._inputCandidates ? 'Add a work item as input...' : 'Click to load work items...';
		return '<option value="">' + tmpLead + '</option>'
			+ tmpList.map((pCandidate) =>
			{
				let tmpID = Number(pCandidate.ID) || 0;
				let tmpNumber = Number(pCandidate.Number) ? ('#' + pCandidate.Number + ' ') : '';
				return '<option value="' + tmpID + '">' + this._esc(tmpNumber + (pCandidate.Title || ('Work item ' + tmpID))) + '</option>';
			}).join('');
	}

	// Fetch the candidate work items the first time the picker is opened (the list can be large, so it is not
	// pulled on every detail load). The candidates endpoint already excludes the ones already linked.
	async loadInputCandidates()
	{
		if (!this._project || this._inputCandidates || !this._connectionsAPI()) { return; }
		let tmpCandidates = [];
		try { tmpCandidates = await this._connectionsAPI().candidates('Project', Number(this._project.IDProject) || 0, 'workiteminputs'); } catch (pError) { tmpCandidates = []; }
		this._inputCandidates = Array.isArray(tmpCandidates) ? tmpCandidates : [];
		let tmpSel = (typeof document !== 'undefined') ? document.getElementById('Project-InputPick') : null;
		if (tmpSel) { tmpSel.innerHTML = this._inputOptionsHTML(); }
	}

	async addInput()
	{
		if (!this._project || !this._connectionsAPI()) { return; }
		let tmpSel = (typeof document !== 'undefined') ? document.getElementById('Project-InputPick') : null;
		let tmpID = tmpSel ? (Number(tmpSel.value) || 0) : 0;
		if (!tmpID) { this._toast('Pick a work item to add first.', 'error'); return; }
		let tmpResult = await this._connectionsAPI().link('Project', Number(this._project.IDProject) || 0, 'workiteminputs', tmpID);
		if (!tmpResult || !tmpResult.Success) { this._toast('Could not add that input.', 'error'); return; }
		this._inputCandidates = null; // the linkable set changed; re-fetch on next open
		await this._reloadInputs();
	}

	async removeInput(pIDWorkItem)
	{
		let tmpID = Number(pIDWorkItem) || 0;
		if (!tmpID || !this._project || !this._connectionsAPI()) { return; }
		let tmpResult = await this._connectionsAPI().unlink('Project', Number(this._project.IDProject) || 0, 'workiteminputs', tmpID);
		if (!tmpResult || !tmpResult.Success) { this._toast('Could not remove that input.', 'error'); return; }
		this._inputCandidates = null;
		await this._reloadInputs();
	}

	async _reloadInputs()
	{
		if (!this._project || !this._connectionsAPI()) { return; }
		let tmpConnections = null;
		try { tmpConnections = await this._connectionsAPI().get('Project', Number(this._project.IDProject) || 0); } catch (pError) { tmpConnections = null; }
		if (tmpConnections) { this._workItemInputs = this._extractInputs(tmpConnections); }
		let tmpEl = (typeof document !== 'undefined') ? document.getElementById('Project-Inputs') : null;
		if (tmpEl) { tmpEl.innerHTML = this._inputsHTML(); }
	}

	openWorkItem(pIDWorkItem)
	{
		let tmpID = Number(pIDWorkItem) || 0;
		if (!tmpID) { return; }
		let tmpRouter = this._router();
		if (tmpRouter && typeof tmpRouter.navigate === 'function') { tmpRouter.navigate('/work-item/' + tmpID); }
	}

	// Upload each chosen file to the Media buffer as TEAM media (so a node persona can read it), then record a
	// ProjectSource pointing at it, with the role the person typed. Each stage retries once; if the corpus
	// create still will not take, the orphaned bytes are dropped so the buffer does not silently fill.
	async attachFiles(pInputEl)
	{
		if (!this._project || !pInputEl || !pInputEl.files || !pInputEl.files.length) { return; }
		let tmpRoleEl = (typeof document !== 'undefined') ? document.getElementById('Project-FileRole') : null;
		let tmpRole = tmpRoleEl ? (String(tmpRoleEl.value || '').trim() || 'Reference') : 'Reference';
		let tmpFiles = Array.prototype.slice.call(pInputEl.files);
		let tmpIDProject = Number(this._project.IDProject) || 0;
		pInputEl.value = '';
		for (let i = 0; i < tmpFiles.length; i++)
		{
			let tmpFile = tmpFiles[i];
			let tmpIDMedia = 0;
			try { tmpIDMedia = await this._retryWrite(() => this._mediaAPI().upload('Project', tmpIDProject, tmpFile, {}).then((pMedia) => Number(pMedia && (pMedia.IDMedia || pMedia.ID)) || 0)); }
			catch (pError) { this._toast('Upload failed for ' + tmpFile.name + ': ' + this._writeErr(pError), 'error'); continue; }
			if (!tmpIDMedia) { this._toast('Upload failed for ' + tmpFile.name + ' (no media id returned).', 'error'); continue; }
			try { await this._retryWrite(() => this._sourceAPI().create({ IDProject: tmpIDProject, Role: tmpRole, IDMedia: tmpIDMedia, Name: String(tmpFile.name || 'file') })); }
			catch (pError)
			{
				try { await this._mediaAPI().remove(tmpIDMedia); } catch (pIgnore) { /* best-effort: do not strand uploaded bytes */ }
				this._toast('Could not add ' + tmpFile.name + ' to the corpus: ' + this._writeErr(pError), 'error');
			}
		}
		await this._reloadCorpus();
	}

	async addData()
	{
		if (!this._project) { return; }
		let tmpRoleEl = (typeof document !== 'undefined') ? document.getElementById('Project-DataRole') : null;
		let tmpNameEl = (typeof document !== 'undefined') ? document.getElementById('Project-DataName') : null;
		let tmpJSONEl = (typeof document !== 'undefined') ? document.getElementById('Project-DataJSON') : null;
		let tmpRole = tmpRoleEl ? (String(tmpRoleEl.value || '').trim() || 'Data') : 'Data';
		let tmpName = tmpNameEl ? String(tmpNameEl.value || '').trim() : '';
		let tmpJSON = tmpJSONEl ? String(tmpJSONEl.value || '').trim() : '';
		if (!tmpJSON) { this._toast('Paste some JSON data first.', 'error'); return; }
		try
		{
			await this._sourceAPI().create({ IDProject: Number(this._project.IDProject) || 0, Role: tmpRole, ContentJSON: tmpJSON, Name: tmpName || 'data' });
		}
		catch (pError) { this._toast((pError && pError.message) || 'Could not add that data.', 'error'); return; }
		await this._reloadCorpus();
	}

	async removeSource(pIDProjectSource)
	{
		let tmpID = Number(pIDProjectSource) || 0;
		if (!tmpID) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpOk = true;
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			try { tmpOk = await tmpModal.confirm('Remove this source from the corpus?', { dangerous: true }); }
			catch (pError) { tmpOk = false; }
		}
		if (!tmpOk) { return; }
		try { await this._sourceAPI().remove(tmpID); }
		catch (pError) { this._toast((pError && pError.message) || 'Could not remove it.', 'error'); return; }
		await this._reloadCorpus();
	}

	// ---- actions that leave for the Ask area / blueprint ---------------------------------------------------
	async runTask(pIDTask)
	{
		if (!this._project || this._busy) { return; }
		this._busy = true;
		let tmpResult = null;
		try { tmpResult = await this._askAPI().submitRequest(Number(pIDTask) || 0, {}, '', [], Number(this._project.IDProject) || 0); }
		catch (pError) { this._busy = false; this._toast((pError && pError.message) || 'Could not start that task.', 'error'); return; }
		this._busy = false;
		let tmpIDRequest = Number(tmpResult && tmpResult.IDRequest) || 0;
		if (!tmpIDRequest) { this._toast('Could not open the run.', 'error'); return; }
		let tmpApp = this._app();
		if (tmpApp && typeof tmpApp.showAskRequest === 'function') { tmpApp.showAskRequest(tmpIDRequest); }
	}

	openRequest(pIDRequest)
	{
		let tmpApp = this._app();
		if (tmpApp && typeof tmpApp.showAskRequest === 'function') { tmpApp.showAskRequest(Number(pIDRequest) || 0); }
	}

	openBlueprint()
	{
		if (!this._project || !(Number(this._project.IDBlueprint) > 0)) { return; }
		let tmpRouter = this._router();
		if (tmpRouter && typeof tmpRouter.navigate === 'function') { tmpRouter.navigate('/blueprint/' + (Number(this._project.IDBlueprint) || 0)); }
	}

	backToList() { this._goList(); }

	// ---- navigation helpers (route through the app so Back / reload / deep-link behave) --------------------
	_goList() { let tmpApp = this._app(); if (tmpApp && typeof tmpApp.showProjects === 'function') { tmpApp.showProjects(); } else { this.showList(); } }
	_goDetail(pID) { let tmpApp = this._app(); if (tmpApp && typeof tmpApp.showProject === 'function') { tmpApp.showProject(Number(pID) || 0); } else { this.showDetail(pID); } }
}

const PictViewProjectConfiguration =
{
	ViewIdentifier: 'Plansheet-Project',
	DefaultRenderable: 'Plansheet-Project',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.prj-wrap { max-width: 900px; margin: 0 auto; padding: 24px 16px 64px; }
		.prj-head-top h1 { margin: 0 0 4px; font-size: 1.6em; color: var(--theme-color-text-primary, #1d2230); }
		.prj-sub { margin: 0 0 20px; color: var(--theme-color-text-secondary, #5b6376); }
		.prj-loading, .prj-empty { color: var(--theme-color-text-secondary, #5b6376); padding: 24px 0; }
		.prj-empty-sm { color: var(--theme-color-text-secondary, #5b6376); font-size: 0.9em; padding: 6px 0; }
		.prj-group-head { font-size: 0.8em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 10px; }
		.prj-new { margin-bottom: 22px; }
		.prj-new-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
		.prj-input { padding: 9px 12px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: var(--theme-color-background-secondary, #f3f5f9); color: var(--theme-color-text-primary, #1d2230); font: inherit; box-sizing: border-box; }
		#Project-NewName { flex: 1 1 260px; }
		.prj-input-kind { width: 110px; }
		.prj-input-role { width: 130px; }
		.prj-data-json { flex: 1 1 240px; resize: vertical; }
		.prj-meta-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 12px 14px; align-items: start; }
		.prj-meta-section { grid-column: 1 / -1; font-size: 0.82em; font-weight: 600; color: var(--theme-color-text-secondary, #5b6376); margin: 2px 0; }
		.prj-meta-field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
		.prj-meta-label { font-size: 0.85em; font-weight: 600; color: var(--theme-color-text-primary, #1d2230); }
		.prj-meta-input { display: block; width: 100%; resize: vertical; line-height: 1.5; }
		.prj-meta-check { width: 18px; height: 18px; }
		.prj-meta-hint { font-size: 0.8em; color: var(--theme-color-text-secondary, #5b6376); line-height: 1.4; }
		.prj-input-pick { flex: 1 1 260px; }
		.prj-src-link { color: var(--theme-color-brand-primary, #3b6fd4); border: none; background: none; cursor: pointer; font: inherit; padding: 0; text-align: left; }
		@media (max-width: 640px) { .prj-meta-field { grid-column: 1 / -1 !important; } }
		.prj-btn { padding: 9px 18px; border: 1px solid var(--theme-color-brand-primary, #3b6fd4); border-radius: 8px; background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; cursor: pointer; font-size: 0.95em; }
		.prj-btn-ghost { background: none; color: var(--theme-color-brand-primary, #3b6fd4); }
		.prj-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; }
		.prj-card { text-align: left; cursor: pointer; padding: 14px 16px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 10px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); }
		.prj-card:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); }
		.prj-card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
		.prj-card-name { font-weight: 600; }
		.prj-card-kind { font-size: 0.78em; color: var(--theme-color-text-secondary, #5b6376); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.03em; }
		.prj-card-sum { font-size: 0.88em; color: var(--theme-color-text-secondary, #5b6376); line-height: 1.4; margin-top: 4px; }
		.prj-back { background: none; border: none; color: var(--theme-color-brand-primary, #3b6fd4); cursor: pointer; padding: 0; font-size: 0.9em; margin-bottom: 12px; }
		.prj-head { display: flex; align-items: center; gap: 10px; }
		.prj-title { margin: 0; font-size: 1.35em; color: var(--theme-color-text-primary, #1d2230); }
		.prj-kind-chip { font-size: 0.72em; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 9px; border-radius: 999px; background: var(--theme-color-background-secondary, #f3f5f9); border: 1px solid var(--theme-color-border-default, #d8dde6); color: var(--theme-color-text-secondary, #5b6376); }
		.prj-sum { color: var(--theme-color-text-secondary, #5b6376); margin: 8px 0 0; line-height: 1.5; }
		.prj-panel { margin-top: 24px; border-top: 1px solid var(--theme-color-border-default, #d8dde6); padding-top: 16px; }
		.prj-src { display: flex; align-items: center; gap: 10px; padding: 7px 10px; border-radius: 8px; }
		.prj-src:hover { background: var(--theme-color-background-hover, #f3f5f9); }
		.prj-role { font-size: 0.72em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 2px 9px; border-radius: 999px; background: var(--theme-color-background-secondary, #f3f5f9); border: 1px solid var(--theme-color-border-default, #d8dde6); color: var(--theme-color-text-secondary, #5b6376); }
		.prj-src-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--theme-color-text-primary, #1d2230); }
		.prj-src-kind { font-size: 0.74em; color: var(--theme-color-text-secondary, #5b6376); }
		.prj-src-del { border: none; background: none; cursor: pointer; color: var(--theme-color-text-secondary, #5b6376); padding: 2px; display: inline-flex; }
		.prj-src-del:hover { color: var(--theme-color-status-error, #c0353a); }
		.prj-src-del svg { width: 15px; height: 15px; }
		.prj-add { display: flex; gap: 8px; flex-wrap: wrap; align-items: flex-start; margin-top: 10px; }
		.prj-file { display: none; }
		.prj-tasks { display: flex; gap: 8px; flex-wrap: wrap; }
		.prj-hint { font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); margin-top: 8px; }
		.prj-reqs { display: flex; flex-direction: column; gap: 2px; }
		.prj-req { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; text-align: left; cursor: pointer; padding: 9px 12px; border: none; border-radius: 8px; background: none; color: var(--theme-color-text-primary, #1d2230); font: inherit; }
		.prj-req:hover { background: var(--theme-color-background-hover, #f3f5f9); }
		.prj-req-name { font-size: 0.92em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.prj-out { display: flex; gap: 10px; padding: 5px 0; align-items: baseline; }
		.prj-out-key { font-weight: 600; color: var(--theme-color-text-primary, #1d2230); min-width: 120px; }
		.prj-out-val { color: var(--theme-color-text-secondary, #5b6376); word-break: break-word; }
		.prj-out-link { color: var(--theme-color-brand-primary, #3b6fd4); }
		.prj-pill { display: inline-block; font-size: 0.62em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 9px; border-radius: 11px; background: #9e9e9e; color: #fff; }
		.prj-pill-draft { background: #9e9e9e; }
		.prj-pill-inprogress, .prj-pill-blueprintready { background: #F6C453; color: #3a2f00; }
		.prj-pill-configready { background: #3b6fd4; }
		.prj-pill-delivered { background: #2E7D32; }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-Project',
			Template: /*html*/`
<div class="prj-wrap">
	<div class="prj-head-top">
		<h1>Projects</h1>
		<p class="prj-sub">Build a form, a sticker, or anything from a cluster of files and the tasks you run against it.</p>
	</div>
	<div id="Project-Body"></div>
</div>`
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Plansheet-Project', TemplateHash: 'Plansheet-Project', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};

module.exports = PictViewProject;
module.exports.default_configuration = PictViewProjectConfiguration;
